import * as React from 'react'
import { FC, useState, useEffect, useRef, useCallback } from 'react'

import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'
import Pills, { Pill } from './fragments/Pills'
import Dropdown from './fragments/Dropdown'
import Section from './fragments/Section'

import Trailer from './sections/Trailer'
import Synopsis from './sections/Synopsis'
import Categories from './sections/Categories'
import Gallery from './sections/Gallery'
import InDepth from './sections/InDepth'
import WatchNow from './sections/WatchNow'

import { useWindowSize } from '../../hooks/useWindowSize'
import { useMobileDesign } from '../../hooks/useMobileDesign'

import './TitleDetails.scss'

import { VMTitle } from '../../index'
import EventRSVP from './sections/EventRSVP'

interface titleContext {
    sectionMinHeight: number
    onInView: (v: string) => void
    titleData: VMTitle
}

const defaultTitle = {
    id: 0,
    image: '',
    url: '',
    title: '',
    trailerCover: '',
    trailerVideo: '',
    synopsis: '',
    gallery: [],
    accolades: [],
    categories: [],
    videos: [],
    listen: [],
    read: [],
    prevTitle: '',
    nextTitle: '',
    titleImage: '',
    titleImageMenu: '',
    watchNowURL: '',
    watchNowImage: '',
}

export const TitleContext = React.createContext<titleContext>({
    sectionMinHeight: 0,
    onInView: () => {},
    titleData: defaultTitle,
    setIntersectionRatio: (section: string, ratio: number) => {},
    getHighestIntersection: () => {},
    intersectionRatios: {}
})

const sections: Pill[] = [
    {
        label: 'Synopsis',
        slug: 'synopsis',
    },
    {
        label: 'Categories',
        slug: 'categories',
    },
    {
        label: 'Event RSVP',
        slug: 'event-rsvp',
    },
    {
        label: 'Watch now',
        slug: 'watch-now',
    },
]

interface Props {
    titleData: VMTitle
}

const TitleDetails: FC<Props> = ({ titleData }) => {
    const scrollRef = useRef<SimpleBar>(null)
    const scrollWrapperRef = useRef<HTMLDivElement>(null)

    const [scrollHeight, setScrollHeight] = useState(0)
    const [currentSection, setCurrentSection] = useState('trailer')
    const [intersectionRatios, setIntersectionRatios] = useState({})
    const isMobile = useMobileDesign()
    const viewPort = useWindowSize()

    const getHighestIntersection = () => {
        let highestRatio = 0;
        let highestSection = null;

        if (intersectionRatios["first"] > 0) {
            return  "synopsis";
        }

        if (intersectionRatios["last"] > 0) {
            return "watch-now";
        }

        for (section in intersectionRatios) {
            if (intersectionRatios[section] >= highestRatio) {
                highestRatio = intersectionRatios[section];
                highestSection = section;
            }
        }

        return highestSection;
    }

    const setIntersectionRatio = (section: string, ratio: number) => {
        if (intersectionRatios[section] !== ratio) {
            setIntersectionRatios({...intersectionRatios, [section]: ratio});
        }

    }

    useEffect(() => {
        setCurrentSection(getHighestIntersection());
        // console.log('intersectionRatios', intersectionRatios, getHighestIntersection())
    }, [intersectionRatios])

    useEffect(() => {
        const scrollWrapperRect = scrollWrapperRef.current?.getBoundingClientRect()
        scrollWrapperRect && setScrollHeight(scrollWrapperRect.height)
    }, [scrollRef, scrollWrapperRef, viewPort])

    useEffect(() => {
        scrollRef.current?.recalculate()
    }, [scrollHeight, scrollRef])

    const scrollToSection = useCallback(
        (slug: string) => {
            const targetSection = document.getElementById(slug)
            if (targetSection) {
                const positionOffset = targetSection.getBoundingClientRect().top
                scrollRef.current?.getScrollElement().scrollTo(0, positionOffset)
                setCurrentSection(slug)
            }
        },
        [scrollRef]
    )

    const onPillClickHandle = useCallback(
        (slug) => {
            scrollToSection(slug)
        },
        [scrollToSection]
    )

    useEffect(() => {
        const hash = window.location.hash.substr(1)
        scrollToSection(hash)
    }, [scrollToSection])

    return (
        <div className="TitleDetails">
            {isMobile ? (
                <Dropdown items={sections} activeSection={currentSection} onClick={onPillClickHandle} />
            ) : (
                <Pills items={sections} activeSection={currentSection} onClick={onPillClickHandle} />
            )}

            <div className="TitleDetails__scroll" ref={scrollWrapperRef}>
                <SimpleBar ref={scrollRef} style={{ height: isMobile ? 'calc(84vh - 106px)' : scrollHeight }}>
                    <TitleContext.Provider
                        value={{
                            sectionMinHeight: scrollHeight,
                            onInView: setCurrentSection,
                            titleData,
                            setIntersectionRatio,
                            getHighestIntersection,
                            intersectionRatios
                        }}
                    >
                        <Section id="first" style={{minHeight: "1px", height: "1px"}}/>
                        <Synopsis />
                        <Categories />
                        <EventRSVP />
                        <WatchNow />
                        <Section id="last" style={{minHeight: "1px", height: "1px"}}/>
                    </TitleContext.Provider>
                </SimpleBar>
            </div>
        </div>
    )
}

export default TitleDetails
